import { useCallback, useEffect, useRef, useState } from "react";
import { Send, X, CheckCircle2, MessageSquareOff, ExternalLink } from "lucide-react";
import { adminApi } from "@/lib/api";

export default function TeamInbox() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(null);
  const [error, setError] = useState("");
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);

  const loadList = useCallback(
    async (c = authed) => {
      if (!c) return;
      try {
        const data = await adminApi.list(c);
        setConvs(data.conversations);
      } catch {
        setAuthed(null);
      }
    },
    [authed]
  );

  const loadActive = useCallback(
    async (cid) => {
      if (!authed || !cid) return;
      try {
        const data = await adminApi.get(authed, cid);
        setActive(data.conversation);
        setMessages(data.messages);
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
        });
      } catch {
        /* conversation may be gone */
      }
    },
    [authed]
  );

  useEffect(() => {
    if (!authed) return;
    const iv = setInterval(() => {
      loadList();
      if (active) loadActive(active.id);
    }, 4000);
    return () => clearInterval(iv);
  }, [authed, active, loadList, loadActive]);

  const unlock = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await adminApi.list(code);
      setAuthed(code);
      const data = await adminApi.list(code);
      setConvs(data.conversations);
    } catch {
      setError("密码错误");
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const t = reply.trim();
    if (!t || !active || busy) return;
    setBusy(true);
    setReply("");
    try {
      await adminApi.reply(authed, active.id, t);
      await loadActive(active.id);
      loadList();
    } finally {
      setBusy(false);
    }
  };

  const doHandoff = async () => {
    if (!active || busy) return;
    setBusy(true);
    try {
      await adminApi.handoff(authed, active.id);
      await loadActive(active.id);
    } finally {
      setBusy(false);
    }
  };

  const doClose = async () => {
    if (!active) return;
    await adminApi.close(authed, active.id);
    setActive(null);
    setMessages([]);
    loadList();
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="inbox-login">
        <form onSubmit={unlock} className="w-full max-w-sm rounded-3xl bg-paper p-8">
          <p className="font-display text-2xl font-semibold text-ink">TKShopMY 客服工作台</p>
          <p className="mt-2 text-sm text-ink/55">内部客服系统。请输入团队密码。</p>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="团队密码"
            className="mt-6 w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-forest"
            data-testid="inbox-passcode-input"
          />
          {error && <p className="mt-2 text-sm text-red-700" data-testid="inbox-error">{error}</p>}
          <button type="submit" className="mt-4 w-full rounded-full bg-forest py-3 text-sm font-semibold text-paper" data-testid="inbox-unlock-button">
            进入工作台
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-paper text-ink" data-testid="inbox-app">
      <aside className={`w-full flex-col border-r border-ink/10 bg-white md:flex md:w-80 ${active ? "hidden" : "flex"}`}>
        <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
          <p className="font-display text-lg font-semibold">客服工作台</p>
          <button onClick={() => setAuthed(null)} className="text-xs font-medium text-ink/50 hover:text-ink" data-testid="inbox-lock">
            锁定
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {convs.length === 0 && <p className="p-5 text-sm text-ink/45">暂时没有对话。</p>}
          {convs.map((c) => (
            <button
              key={c.id}
              onClick={() => loadActive(c.id)}
              className={`block w-full border-b border-ink/5 px-5 py-4 text-left transition-colors hover:bg-paper ${
                active?.id === c.id ? "bg-paper" : ""
              }`}
              data-testid={`inbox-conv-${c.id.slice(0, 8)}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold">
                  {c.answers?.product || "访客"} · {c.visitor_id.slice(0, 6)}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    c.status === "pending_agent"
                      ? "bg-clay/15 text-clay"
                      : c.status === "agent"
                        ? "bg-lime/30 text-forest"
                        : "bg-sand text-ink/50"
                  }`}
                >
                  {c.status === "pending_agent" ? "待回复" : c.status}
                </span>
              </div>
              <p className="mt-1 truncate text-xs text-ink/50">{c.last_message}</p>
              <p className="mt-1 text-[10px] text-ink/35">
                {new Date(c.updated_at).toLocaleString("zh-Hans", { hour12: true })}
                {c.qualified ? " · 已筛选" : ""}
              </p>
            </button>
          ))}
        </div>
      </aside>

      <main className={`flex-1 flex-col md:flex ${active ? "flex" : "hidden"}`}>
        {active ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink/10 bg-white px-5 py-3.5">
              <div>
                <p className="text-sm font-bold">
                  {active.answers?.product || "新访客"}
                  {active.qualified && <CheckCircle2 size={14} className="ml-1.5 inline text-moss" />}
                </p>
                <p className="text-xs text-ink/50">
                  {active.answers?.stage || "—"} · 需求：{active.answers?.need || "—"} · 市场：{active.answers?.market || "—"}
                  {active.answers?.service ? ` · 服务：${active.answers.service}` : ""}
                </p>
                {active.answers?.challenge && (
                  <p className="mt-0.5 text-xs text-ink/50">头痛问题：{active.answers.challenge}</p>
                )}
                <p className="mt-0.5 text-[11px] text-ink/40">
                  来源页面：{active.page || "/"}
                  {active.utm?.utm_source ? ` · ${[active.utm.utm_source, active.utm.utm_medium, active.utm.utm_campaign].filter(Boolean).join(" / ")}` : ""}
                  {active.utm?.gclid ? " · gclid ✓" : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={doHandoff}
                  disabled={busy || active.handoff_sent}
                  className="flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-xs font-semibold text-paper disabled:opacity-40"
                  data-testid="inbox-handoff-button"
                >
                  <ExternalLink size={12} />
                  {active.handoff_sent ? "已发送 WhatsApp" : "发送 WhatsApp 接力"}
                </button>
                <button
                  onClick={doClose}
                  className="flex items-center gap-1.5 rounded-full border border-ink/15 px-4 py-2 text-xs font-semibold text-ink/60 hover:text-ink"
                  data-testid="inbox-close-button"
                >
                  <MessageSquareOff size={12} />
                  结束对话
                </button>
                <button onClick={() => setActive(null)} className="rounded-full p-2 md:hidden" aria-label="Back to list">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-5" data-testid="inbox-messages">
              {messages.map((m, i) => (
                <div key={m.id || i} className={`flex ${m.sender === "visitor" ? "justify-start" : "justify-end"}`}>
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                      m.sender === "visitor"
                        ? "rounded-bl-sm border border-ink/10 bg-white"
                        : m.sender === "agent"
                          ? "rounded-br-sm bg-forest text-paper"
                          : "rounded-br-sm bg-sand text-ink/80"
                    }`}
                  >
                    <p className="mb-0.5 text-[10px] font-bold uppercase tracking-wide opacity-60">
                      {m.sender === "bot" ? "机器人" : m.sender === "agent" ? "你" : "访客"}
                    </p>
                    <p className="whitespace-pre-line">{m.text}</p>
                    {m.link?.url && (
                      <a href={m.link.url} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs underline">
                        {m.link.label}: {m.link.url}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={sendReply} className="flex items-center gap-2 border-t border-ink/10 bg-white px-4 py-3">
              <input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="以 TKShopMY 团队身份回复…"
                className="flex-1 rounded-full bg-paper px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-forest"
                data-testid="inbox-reply-input"
              />
              <button
                type="submit"
                disabled={busy || !reply.trim()}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-forest text-paper disabled:opacity-40"
                aria-label="Send reply"
                data-testid="inbox-send-button"
              >
                <Send size={16} />
              </button>
            </form>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center text-sm text-ink/40">
            选择左侧对话，开始回复。
          </div>
        )}
      </main>
    </div>
  );
}
